/**
 * User Profile Section Component
 *
 * @module modules/user/presentation/shared/UserProfileSection
 *
 * User profile section with theme toggle and dropdown menu.
 * Includes logout functionality and responsive design.
 *
 * Components used:
 * - UserAvatar: Avatar with initials fallback
 * - UserInfo: Name and status display
 * - ProfileMenuDropdown: Dropdown menu with profile actions
 */

import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import PhoneIcon from '@mui/icons-material/Phone';
import SettingsIcon from '@mui/icons-material/Settings';
import { useState, useRef, memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLogout } from '@/modules/auth/application/hooks';
import { useAuthStore } from '@/modules/auth/application/store';
import { useClearUserCache } from '@/modules/user/application';
import { APP_ROUTES } from '@/shared/constants';
import { useToast } from '@/shared/components';
import { SettingsDialog } from '@/shared/components/settings';
import { useTheme } from '@/shared/providers/ThemeProvider';
import { AppException } from '@/core/exceptions';

import { ProfileMenuDropdown, UserAvatar, UserInfo, TestCallDialog } from './components';
import type { ProfileMenuItem } from './types';

// ============================================================================
// Types
// ============================================================================

interface UserProfileSectionProps {
  /** Whether the sidebar is collapsed */
  collapsed: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * User Profile Section Component
 *
 * Displays the user profile section at the bottom of the sidebar.
 * Features:
 * - User avatar with initials
 * - User name and status (when expanded)
 * - Dropdown menu with profile actions
 * - Theme toggle
 * - Logout functionality
 * - Responsive design (icons only when collapsed)
 *
 * @example
 * ```tsx
 * <UserProfileSection collapsed={isSidebarCollapsed} />
 * ```
 */
export const UserProfileSection = memo(function UserProfileSection({ collapsed }: UserProfileSectionProps) {
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();
  const { toggleTheme, isDark } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [testCallOpen, setTestCallOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Get user from auth store
  const user = useAuthStore(state => state.user);

  const toast = useToast();

  // Logout mutation
  const { mutate: logout, isPending: isLoggingOut } = useLogout();

  // Clear cache mutation
  const { mutate: clearCache } = useClearUserCache();

  // Handle clear cache
  const handleClearCache = useCallback(() => {
    clearCache(undefined, {
      onSuccess: () => {
        toast.success('Cache cleared successfully');
      },
      onError: (error) => {
        const message = error instanceof AppException ? error.userMessage : error instanceof Error ? error.message : 'Failed to clear cache';
        toast.error(message);
      },
    });
  }, [clearCache, toast]);

  // Handle logout
  const handleLogout = useCallback(() => {
    logout(undefined, {
      onSuccess: () => {
        navigate(APP_ROUTES.AUTH.ROOT, { replace: true });
      },
    });
  }, [logout, navigate]);

  // Define menu items with theme toggle
  const profileMenuItems: ProfileMenuItem[] = [
    {
      label: isDark ? 'Dark Mode' : 'Light Mode',
      icon: isDark ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />,
      isThemeToggle: true,
    },
    {
      label: 'Test Call',
      icon: <PhoneIcon fontSize="small" />,
      onClick: () => setTestCallOpen(true),
    },
    {
      label: 'Settings',
      icon: <SettingsIcon fontSize="small" />,
      onClick: () => setSettingsOpen(true),
    },
    { label: 'Clear Cache', icon: <DeleteSweepIcon fontSize="small" />, onClick: handleClearCache },
    { label: 'Logout', icon: <LogoutIcon fontSize="small" />, isLogout: true },
  ];

  const handleToggle = () => {
    setExpanded(prev => !prev);
  };

  const handleClose = () => {
    setExpanded(false);
  };

  const handleMenuItemClick = useCallback(
    (item: ProfileMenuItem, e: React.MouseEvent) => {
      if (item.isThemeToggle) {
        e.stopPropagation();
        toggleTheme();
        // Don't close the menu when toggling theme
        return;
      }
      if (item.isLogout) {
        handleLogout();
        handleClose();
        return;
      }
      if (item.onClick) {
        item.onClick();
      }
      handleClose();
    },
    [toggleTheme, handleLogout]
  );

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box
        sx={{
          flexShrink: 0,
          borderTop: 1,
          borderColor: 'divider',
          position: 'relative',
          bgcolor: alpha(muiTheme.palette.background.default, 0.5),
        }}
      >
        {/* User Profile Button - Clickable to toggle dropdown */}
        <Box
          ref={anchorRef}
          sx={{
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            borderRadius: 1.5,
            mx: 0.5,
            my: 0.5,
            transition: muiTheme.transitions.create('background-color'),
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
          onClick={handleToggle}
          role="button"
          aria-expanded={expanded}
          aria-haspopup="menu"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggle();
            }
          }}
        >
          <UserAvatar name={user?.name || user?.displayName} avatarUrl={user?.avatar || user?.photoUrl} size={32} showTooltip={collapsed} tooltipText={user?.name || user?.displayName || 'User'} />
          {!collapsed && (
            <>
              <UserInfo name={user?.name || user?.displayName} email={user?.email} extensionNumber={user?.extensionNumber} />
              <ExpandLessIcon
                sx={{
                  fontSize: 16,
                  color: 'text.secondary',
                  transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: muiTheme.transitions.create('transform'),
                }}
              />
            </>
          )}
        </Box>

        {/* Dropdown Menu - Positioned above the profile */}
        <ProfileMenuDropdown
          open={expanded}
          anchorEl={anchorRef.current}
          items={profileMenuItems}
          collapsed={collapsed}
          isDark={isDark}
          isLoggingOut={isLoggingOut}
          onItemClick={handleMenuItemClick}
        />

        <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
        <TestCallDialog open={testCallOpen} onClose={() => setTestCallOpen(false)} />
      </Box>
    </ClickAwayListener>
  );
});
